import {
  asIssueFieldSelection,
  hasMultiSelectOptionIds,
  type IssueFieldSelection,
  type IssueFieldSelectionValues,
  type IssueFieldSingleSelectOption,
  type IssueFieldType,
  toIssueFieldType,
} from '@github-ui/issue-fields-shared/issue-field'
import type {
  IssueDateFieldWithValue,
  IssueFieldWithUnsavedValue,
  IssueMultiSelectFieldWithValue,
  IssueNumberFieldWithValue,
  IssueSingleSelectFieldWithValue,
  IssueTextFieldWithValue,
} from '@github-ui/issue-metadata-types/issue-field-with-unsaved-value'
import type {IssueField} from '@github-ui/item-picker/IssueFieldPicker'

import type {SafeFieldValue} from '../components/fields/IssueFieldEditors'
import {formatDateString, stripTimezone} from './date-formatting'

export function isTextField(field: IssueField): field is IssueField & {dataType: 'TEXT'} {
  return field.dataType === 'TEXT'
}

export function isSingleSelectField(field: IssueField): field is IssueField & {dataType: 'SINGLE_SELECT'} {
  return field.dataType === 'SINGLE_SELECT'
}

export function isMultiSelectField(field: IssueField): field is IssueField & {dataType: 'MULTI_SELECT'} {
  return field.dataType === 'MULTI_SELECT'
}

export function isDateField(field: IssueField): field is IssueField & {dataType: 'DATE'} {
  return field.dataType === 'DATE'
}

export function isNumberField(field: IssueField): field is IssueField & {dataType: 'NUMBER'} {
  return field.dataType === 'NUMBER'
}

export function isIssueFieldWithUnsavedValue(field: IssueFieldWithUnsavedValue): field is IssueTextFieldWithValue {
  return isTextField(field.field)
}

export function isSingleSelectFieldWithValue(
  field: IssueFieldWithUnsavedValue,
): field is IssueSingleSelectFieldWithValue {
  return isSingleSelectField(field.field) && field.value !== null
}

// Extract sorted option IDs from a multi-select field value.
// Handles both relay data (options array) and mutation input (multiSelectOptionIds).
function getMultiSelectOptionIds(fieldValue: SafeFieldValue | null | undefined): string[] {
  if (!fieldValue) return []
  if (hasMultiSelectOptionIds(fieldValue)) return [...fieldValue.multiSelectOptionIds].sort()
  if (Array.isArray(fieldValue.options)) return fieldValue.options.map((o: {id: string}) => o.id).sort()
  return []
}

// Check if two field values are equal based on field type
export function areFieldValuesEqual(
  initialField: SafeFieldValue | null | undefined,
  currentField: SafeFieldValue | null | undefined,
): boolean {
  if (initialField?.field?.id !== currentField?.field?.id) return false

  if (!initialField?.field || !currentField?.field) return false

  const fieldForTypeCheck = initialField.field as IssueField

  if (isTextField(fieldForTypeCheck) || isDateField(fieldForTypeCheck)) {
    return initialField?.value === currentField?.value
  }

  if (isNumberField(fieldForTypeCheck)) {
    return initialField?.numberValue === currentField?.numberValue
  }

  if (isSingleSelectField(fieldForTypeCheck)) {
    return initialField?.name === currentField?.name
  }

  if (isMultiSelectField(fieldForTypeCheck)) {
    const initialIds = getMultiSelectOptionIds(initialField)
    const currentIds = getMultiSelectOptionIds(currentField)
    if (initialIds.length !== currentIds.length) return false
    return initialIds.every((id, index) => id === currentIds[index])
  }

  return true
}

// Categorize field changes into added, updated, and removed fields
export function getFieldChanges(
  initialValues: SafeFieldValue[],
  currentValues: SafeFieldValue[],
): {
  addedFields: SafeFieldValue[]
  updatedFields: SafeFieldValue[]
  removedFields: SafeFieldValue[]
} {
  const addedFields = currentValues.filter(
    fieldValue => !initialValues.some(initialFieldValue => initialFieldValue?.field?.id === fieldValue?.field?.id),
  )

  const updatedFields = currentValues.filter(fieldValue =>
    initialValues.some(
      initialFieldValue =>
        initialFieldValue?.field?.id === fieldValue?.field?.id && !areFieldValuesEqual(initialFieldValue, fieldValue),
    ),
  )

  const removedFields = initialValues.filter(
    initialFieldValue => !currentValues.some(fv => fv?.field?.id === initialFieldValue?.field?.id),
  )

  return {addedFields, updatedFields, removedFields}
}

// Create a cleared field value
export function createClearedFieldValue(
  fieldValue: SafeFieldValue | null | undefined,
): SafeFieldValue | null | undefined {
  if (!fieldValue || !fieldValue.field) return fieldValue

  // For all field types, return cleared value
  return toIssueFieldWithUnsavedValue(fieldValue.field, null) as SafeFieldValue
}

export function toIssueFieldWithUnsavedValue(
  selectedField: IssueField | IssueFieldSelection,
  value: IssueFieldSelectionValues,
): IssueFieldWithUnsavedValue {
  const field = asIssueFieldSelection(selectedField)
  switch (field.dataType) {
    case 'text':
      return {
        field: {...field, dataType: 'TEXT'},
        value: (value || '') as string,
      }
    case 'single_select':
      return {
        field: {...field, dataType: 'SINGLE_SELECT'},
        value: value as IssueFieldSingleSelectOption | null,
      }
    case 'multi_select':
      return {
        field: {...field, dataType: 'MULTI_SELECT'},
        value: value as IssueFieldSingleSelectOption[] | null,
      }
    case 'date':
      return {
        field: {...field, dataType: 'DATE'},
        value: value as Date | null,
      }
    case 'number': {
      return {
        field: {...field, dataType: 'NUMBER'},
        value: value as number | null,
      }
    }
  }
}

export function toIssueFieldWithValue(
  selectedField: IssueField | IssueFieldSelection,
  value: IssueFieldSelectionValues,
  readonly?: boolean,
) {
  const field = asIssueFieldSelection(selectedField)
  switch (field.dataType) {
    case 'text':
      return {
        field: {...field, dataType: 'TEXT'},
        value: value as string,
        readonly,
      } as IssueTextFieldWithValue
    case 'single_select':
      return {
        field: {...field, dataType: 'SINGLE_SELECT'},
        value: value as IssueFieldSingleSelectOption | null,
        readonly,
      } as IssueSingleSelectFieldWithValue
    case 'multi_select':
      return {
        field: {...field, dataType: 'MULTI_SELECT'},
        value: value as IssueFieldSingleSelectOption[] | null,
        readonly,
      } as IssueMultiSelectFieldWithValue
    case 'date':
      return {
        field: {...field, dataType: 'DATE'},
        value: value as Date | null,
        readonly,
      } as IssueDateFieldWithValue
    case 'number': {
      // If value is null or empty string, keep it as null (for deletion)
      if (value === null || value === '') {
        return {
          field: {...field, dataType: 'NUMBER'},
          value: null,
          readonly,
        } as IssueNumberFieldWithValue
      }
      const number = Number(value)
      return {
        field: {...field, dataType: 'NUMBER'},
        value: isNaN(number) ? 0 : number,
        readonly,
      } as IssueNumberFieldWithValue
    }
  }
}

export function getDeleteValue(fieldType: IssueFieldType, value?: string) {
  switch (fieldType) {
    case 'text':
      return value === ''
    case 'single_select':
      return value === undefined
    case 'multi_select':
      return value === undefined || value === ''
    case 'date':
      return value === undefined
    case 'number':
      return value === ''
    default:
      throw new Error(`Unsupported field type: ${fieldType}`)
  }
}

export function getIssueFieldSelectionValue(fieldType: IssueFieldType, value: IssueFieldSelectionValues) {
  switch (fieldType) {
    case 'text':
      return value as string | undefined
    case 'single_select': {
      const singleSelectOption = value as IssueFieldSingleSelectOption | null
      return singleSelectOption && !!singleSelectOption.id ? String(singleSelectOption.id) : undefined
    }
    case 'multi_select': {
      const multiSelectOptions = value as IssueFieldSingleSelectOption[] | null
      const optionIds = multiSelectOptions?.filter(option => !!option.id).map(option => String(option.id))
      return optionIds?.length ? optionIds.join(',') : undefined
    }
    case 'date':
      return formatDateForStorage(value as Date | null) || undefined
    case 'number': {
      return value?.toString()
    }
  }
}

export function getIssueFieldSelectionPresentationValue(fieldType: IssueFieldType, value: IssueFieldSelectionValues) {
  if (fieldType === 'single_select') {
    const singleSelectOption = value as IssueFieldSingleSelectOption | null
    return singleSelectOption && !!singleSelectOption.name ? String(singleSelectOption.name) : undefined
  } else if (fieldType === 'multi_select') {
    const options = value as IssueFieldSingleSelectOption[] | null
    if (options === null) return undefined
    if (options.length === 0) return ''
    return options
      .filter(o => !!o.name)
      .map(o => o.name)
      .join(', ')
  } else if (fieldType === 'date') {
    // Normalizes the date by setting the time to noon UTC (12:00 PM)
    const formatted = formatDateForStorage(value as Date | null)
    // Creates a new Date object from that normalized ISO string
    const dateValue = formatted ? stripTimezone(formatted) : null
    // Uniform formatting for presentation
    return dateValue ? formatDateString(dateValue) : undefined
  }
  return getIssueFieldSelectionValue(fieldType, value)
}

export function isDateFieldWithValue(field: IssueFieldWithUnsavedValue): field is IssueDateFieldWithValue {
  return isDateField(field.field) && field.value !== null
}

// Helper function to format dates for backend storage
// The date is already normalized to noon UTC by IssueFieldDateEditor
// Backend will extract just the date portion using Ruby's Date class
export function formatDateForStorage(date: Date | null): string | null {
  if (!date) return null
  return date.toISOString()
}

// Raw issue field values from the duplication fragments
export type RawIssueFieldValueNode = {
  field?: {
    id?: string | null
    name?: string | null
    dataType?: string | null
  } | null
  value?: string | null
  optionId?: string | null
  singleSelectName?: string | null
  name?: string | null
  color?: string | null
  description?: string | null
  numberValue?: number | null
}

// Parses raw issue field values into IssueFieldWithUnsavedValue entries
export function parseIssueFieldValues(
  nodes: ReadonlyArray<RawIssueFieldValueNode | null | undefined>,
): IssueFieldWithUnsavedValue[] {
  return nodes.flatMap(node => {
    if (!node?.field?.id || !node.field.name || !node.field.dataType) return []
    const fieldType = toIssueFieldType(node.field.dataType)
    let value: IssueFieldSelectionValues | null = null
    switch (fieldType) {
      case 'text':
        value = node.value ?? ''
        break
      case 'single_select': {
        const selectedName = node.singleSelectName ?? node.name
        if (selectedName && node.optionId && node.color) {
          value = {
            id: node.optionId,
            name: selectedName,
            description: node.description ?? null,
            color: node.color,
          } as IssueFieldSingleSelectOption
        }
        break
      }
      case 'date':
        if (node.value) {
          value = new Date(node.value)
        }
        break
      case 'number':
        if (node.numberValue != null) {
          value = Number(node.numberValue)
        }
        break
    }
    if (value === null && fieldType !== 'text') return []
    const field = {id: node.field.id, name: node.field.name, dataType: fieldType}
    return [toIssueFieldWithUnsavedValue(field, value)]
  })
}
